import { useSnackbar } from "@gemini/ui"
import { zodResolver } from "@hookform/resolvers/zod"
import { useState } from "react"
import { useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"

import {
  ContactLanguageType,
  ContactType,
  Form,
  formKeyValues,
  germanBrands,
  getContactFormValidationSchema,
} from "@aviv-ipp/bff-types"

import { postContactRequest } from "../../api/postContactRequest"
import { useIppContext } from "../../context/IppContext"
import { useContactForm } from "../../hooks/useContactForm"
import { TRACKING_VALUES, useSubmitForm, useSubmittedForm } from "../../utils/trackingEvents"

export const getFirstErrorField = (errors: Form) => {
  for (const field of formKeyValues) {
    if (Object.keys(errors.contactRequest?.personContact ?? {}).includes(field)) {
      return field
    }
  }
  if (errors.contactRequest?.message) {
    return "message"
  }
  return undefined
}

export const useContactFormValidation = () => {
  const { t, i18n } = useTranslation()
  const { add } = useSnackbar()
  const {
    title,
    contactContext,
    contactType: contactTypeSelected,
    setContactType,
    isOpen,
    setIsOpen,
    isError,
    setIsError,
    customPlaceholder,
  } = useContactForm()
  const [isLoading, setIsLoading] = useState(false)
  const { bffBaseUrl, legacyCustomerId, brand } = useIppContext()
  const isGermanBrand = germanBrands.includes(brand.toLowerCase() as any)

  const { control, trigger, reset, setValue, getValues, formState } = useForm<Form>({
    resolver: zodResolver(getContactFormValidationSchema(isGermanBrand)),
  })
  if (!isGermanBrand) setValue("contactRequest.personContact.salutation", "NONE")
  const trackingSubmitAvZone = contactContext ?? TRACKING_VALUES.ZONE[contactContext]
  const eventSubmitForm = useSubmitForm(trackingSubmitAvZone)
  const eventSubmittedForm = useSubmittedForm()

  const handleSubmit = () => {
    if (!formState.isValid) return

    const formValues = getValues()

    formValues.contactRequest.personContact.language =
      i18n.language?.toUpperCase() as ContactLanguageType

    setIsLoading(true)
    setIsError(false)
    eventSubmitForm(contactTypeSelected as ContactType)
    postContactRequest(
      {
        ...formValues,
        project: contactTypeSelected as ContactType,
        contactRequest: formValues.contactRequest,
      },
      bffBaseUrl,
      legacyCustomerId,
      brand
    )
      .then(() => {
        eventSubmittedForm(contactTypeSelected as ContactType)
        add({
          description: t("contactAgency_sendMessageToastConfirmation"),
          action: { children: "" },
          variant: "success",
        })
        reset(undefined, { keepDefaultValues: true })
        setIsOpen(false)
        setIsLoading(false)
      })
      .catch((error: any) => {
        console.log(error.message)
        setIsLoading(false)
        setIsError(true)
      })
  }

  return {
    title,
    contactTypeSelected,
    formState,
    setContactType,
    isOpen,
    setIsOpen,
    isLoading,
    isError,
    setIsError,
    reset,
    control,
    trigger,
    setValue,
    handleSubmit,
    firstInputError: getFirstErrorField(formState.errors as unknown as Form),
    customPlaceholder,
  } as const
}
